// LB-17 Ask-to-buy — the pure part. A child names something they want and its price; this
// module answers the money questions every surface needs, so the kid sheet, the parent card
// and the check script all read the SAME arithmetic:
//   - can the child cover it now (tokens first, then the Savings Vault — goalProgress's rule)?
//   - if not, how short are they, and how does a parent's gift or loan close the gap?
//   - which ask is open, what happened to the last one, and is a loan still being paid back?
// Nothing here touches state. The writes live in RequestsTab (parent decisions) and
// ChildView/AskToBuy (the child's ask), on the shared addTransaction/submitRequest paths.
import { goalProgress, goalTargetTokens, penceToTokens, pickTopGoal, resolveRedeemSplit, tokensToPence } from "./utils.js";

export const ASK_TO_BUY_TYPE = "ask_to_buy";

// Requests of this type resolve to one of: "approved" (the child could cover it, or a
// parent covered the gap — see gapCover), "goal" (someone turned it into a goal instead) or
// "rejected". "goal" is a real status because history must not read "rejected" for a child
// who was told "keep saving — it's a goal now".
export const ASK_STATUS_GOAL = "goal";

// The child's whole spending power in tokens, and how far a price is beyond it.
export function askAvailability(child, tokensNeeded, rate) {
  const vaultTokens = penceToTokens(child?.vaultBalancePence || 0, rate);
  const availableTokens = (child?.tokenBalance || 0) + vaultTokens;
  const shortfall = Math.max(0, tokensNeeded - availableTokens);
  return { availableTokens, vaultTokens, shortfall, affordable: tokensNeeded > 0 && shortfall === 0 };
}

// What buying does to the goal Home shows — the Till's own three-case line, lifted so the
// kid's ask screen and the parent's card quote the same numbers. null when there is no goal
// or the child cannot cover the price (then the shortfall is the line).
export function askImpact(child, tokensNeeded, rate, split) {
  const goal = pickTopGoal(child.goals, child, rate);
  if (!goal || !split) return null;
  const vaultPence = split.vaultPart > 0 ? tokensToPence(split.vaultPart, rate) : 0;
  const before = goalProgress(child, goalTargetTokens(goal, rate), rate).pct;
  const after = goalProgress(
    { ...child, tokenBalance: child.tokenBalance - split.tokenPart, vaultBalancePence: (child.vaultBalancePence || 0) - vaultPence },
    goalTargetTokens(goal, rate),
    rate
  ).pct;
  return { title: goal.title, before, after };
}

// The child's own money goes first, tokens before the vault (Alan, 28 Sep). For an
// affordable price this is exactly resolveRedeemSplit. For a shortfall the child's tokens
// and the WHOLE vault are used and the parent's gift or loan covers `gap`:
//   gift — `gap` tokens are credited first, then the full price is spent (tokens = own
//          tokens + gift, vault = everything in it).
//   loan — the full price is spent as it stands: the token side goes `gap` below zero and
//          the child's earnings bring it back (no separate ledger — the negative balance IS
//          the loan, and the request row records how much it was).
export function gapSplit(child, tokensNeeded, rate) {
  const { vaultTokens, shortfall } = askAvailability(child, tokensNeeded, rate);
  if (shortfall === 0) {
    const split = resolveRedeemSplit(child, tokensNeeded, rate, "token");
    return { ...split, vaultPence: split.vaultPart > 0 ? tokensToPence(split.vaultPart, rate) : 0, gap: 0 };
  }
  const vaultPart = Math.min(vaultTokens, tokensNeeded);
  const tokenPart = tokensNeeded - vaultPart; // = own tokens + gap
  return { tokenPart, vaultPart, vaultPence: vaultPart > 0 ? tokensToPence(vaultPart, rate) : 0, gap: shortfall };
}

// One open ask per child (Alan, 28 Sep — "one open spend request it is").
export function openAsk(requests, childId) {
  return (requests || []).find((r) => r && r.requestType === ASK_TO_BUY_TYPE && r.childId === childId && r.status === "pending") || null;
}

// The most recent ask of any status — what the Rewards card shows until the next one is sent.
export function latestAsk(requests, childId) {
  const mine = (requests || []).filter((r) => r && r.requestType === ASK_TO_BUY_TYPE && r.childId === childId);
  if (mine.length === 0) return null;
  return mine.reduce((best, r) => (new Date(r.timestamp) > new Date(best.timestamp) ? r : best), mine[0]);
}

// "pending" | "approved" | "gift" | "loan" | "goal" | "declined"
export function askOutcome(req) {
  if (!req) return null;
  if (req.status === "pending") return "pending";
  if (req.status === ASK_STATUS_GOAL) return "goal";
  if (req.status === "approved") return req.gapCover === "gift" ? "gift" : req.gapCover === "loan" ? "loan" : "approved";
  return "declined";
}

// A loan is open while the token balance is below zero after a loan-funded approval. Returns
// who lent it (for the hero line) and how much, or null.
export function openLoan(requests, child) {
  if (!child || !(child.tokenBalance < 0)) return null;
  const loans = (requests || []).filter((r) => r && r.requestType === ASK_TO_BUY_TYPE && r.childId === child.id && r.status === "approved" && r.gapCover === "loan" && !r.reversed);
  if (loans.length === 0) return null;
  const last = loans.reduce((best, r) => (new Date(r.resolvedAt || r.timestamp) > new Date(best.resolvedAt || best.timestamp) ? r : best), loans[0]);
  return { adultName: last.adultName || "", tokens: last.gapTokens || 0, owed: -child.tokenBalance };
}

// The parent decisions a child is told about on Home (DecisionCard, Alan 28 Sep): a yes on a
// goal redemption or a reward claim, and every outcome of an ask-to-buy (yes, gift, loan,
// made a goal, or a no). Newest first, by when it was decided. Undone rows are left out.
const DECISION_TYPES = ["goal_redeem", "experience_redeem", ASK_TO_BUY_TYPE];
export function decisionRequests(requests, childId) {
  return (requests || [])
    .filter((r) => r && r.childId === childId && DECISION_TYPES.includes(r.requestType) && !r.reversed)
    .filter((r) => r.status === "approved" || (r.requestType === ASK_TO_BUY_TYPE && (r.status === "rejected" || r.status === ASK_STATUS_GOAL)))
    .sort((a, b) => new Date(b.resolvedAt || b.timestamp) - new Date(a.resolvedAt || a.timestamp));
}

// A goal redemption and a reward claim carry their name behind a fixed prefix (App.jsx
// submitRequest callers); the card wants the name alone.
export function decisionItemName(req) {
  return String(req?.title || "").replace(/^Redeem (goal|reward): /, "");
}
